/**
 * Parse artifact blocks from message content
 */
export interface DocumentArtifact {
  type: 'docx' | 'pdf' | 'xlsx' | 'csv';
  filename: string;
  content: string;
}

export function parseArtifacts(content: string): { cleanContent: string; artifacts: DocumentArtifact[] } {
  const artifactRegex = /\[DOCUMENT:(\w+):(.+?)\]([\s\S]*?)\[\/DOCUMENT\]/g;
  const artifacts: DocumentArtifact[] = [];
  
  let match;
  while ((match = artifactRegex.exec(content)) !== null) {
    artifacts.push({
      type: match[1] as DocumentArtifact['type'],
      filename: match[2].trim(),
      content: match[3].trim(),
    });
  }
  
  const cleanContent = content.replace(artifactRegex, '').trim();
  
  return { cleanContent, artifacts };
}

/**
 * Parse markdown content into structured elements
 */
function parseMarkdown(markdown: string): Array<{ type: 'heading' | 'paragraph' | 'list' | 'table'; level?: number; content: string; rows?: string[][] }> {
  const lines = markdown.split('\n');
  const elements: Array<{ type: 'heading' | 'paragraph' | 'list' | 'table'; level?: number; content: string; rows?: string[][] }> = [];
  
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    
    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      elements.push({ type: 'heading', level: headingMatch[1].length, content: headingMatch[2] });
      i++;
      continue;
    }
    
    // Tables
    if (line.includes('|') && i + 1 < lines.length && lines[i + 1].includes('|') && lines[i + 1].includes('-')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|')) {
        const row = lines[i].split('|').filter(cell => cell.trim() !== '').map(cell => cell.trim());
        if (!lines[i].match(/^\s*\|?\s*[-:]+\s*\|/)) { // Skip separator row
          rows.push(row);
        }
        i++;
      }
      if (rows.length > 0) {
        elements.push({ type: 'table', content: '', rows });
      }
      continue;
    }
    
    // List items
    if (line.match(/^[\s]*[-*]\s+/)) {
      elements.push({ type: 'list', content: line.replace(/^[\s]*[-*]\s+/, '• ') });
      i++;
      continue;
    }
    
    // Paragraphs
    if (line.trim()) {
      elements.push({ type: 'paragraph', content: line });
    }
    i++;
  }
  
  return elements;
}

/**
 * Convert markdown content to a Word document
 * Uses dynamic import to reduce initial bundle size
 */
export async function convertToDocx(markdown: string, filename: string): Promise<Blob> {
  // Dynamic import - only loads when function is called
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType } = await import('docx');
  
  const elements = parseMarkdown(markdown);
  const children: (InstanceType<typeof Paragraph> | InstanceType<typeof Table>)[] = [];
  
  for (const element of elements) {
    if (element.type === 'heading') {
      const headingLevel = element.level === 1 ? HeadingLevel.HEADING_1 
        : element.level === 2 ? HeadingLevel.HEADING_2 
        : HeadingLevel.HEADING_3;
      
      children.push(new Paragraph({
        text: element.content,
        heading: headingLevel,
        spacing: { before: 240, after: 120 },
      }));
    } else if (element.type === 'table' && element.rows) {
      const tableRows = element.rows.map((row, rowIndex) => 
        new TableRow({
          children: row.map(cell => 
            new TableCell({
              children: [new Paragraph({ 
                children: [new TextRun({ 
                  text: cell, 
                  bold: rowIndex === 0 
                })]
              })],
              width: { size: 100 / row.length, type: WidthType.PERCENTAGE },
            })
          ),
        })
      );
      
      children.push(new Table({
        rows: tableRows,
        width: { size: 100, type: WidthType.PERCENTAGE },
      }));
    } else if (element.type === 'list') {
      children.push(new Paragraph({
        children: [new TextRun({ text: element.content })],
        spacing: { before: 60, after: 60 },
        indent: { left: 720 },
      }));
    } else {
      children.push(new Paragraph({
        children: [new TextRun({ text: element.content })],
        spacing: { before: 120, after: 120 },
      }));
    }
  }
  
  const doc = new Document({
    sections: [{
      properties: {},
      children,
    }],
  });
  
  return await Packer.toBlob(doc);
}

/**
 * Convert markdown content to PDF
 * Uses dynamic import to reduce initial bundle size
 */
export async function convertToPdf(markdown: string, filename: string): Promise<Blob> {
  // Dynamic import - only loads when function is called
  const { default: jsPDF } = await import('jspdf');
  
  const pdf = new jsPDF();
  const elements = parseMarkdown(markdown);
  
  let y = 20;
  const margin = 20;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const maxWidth = pageWidth - margin * 2;
  
  for (const element of elements) {
    // Check if we need a new page
    if (y > pdf.internal.pageSize.getHeight() - 30) {
      pdf.addPage();
      y = 20;
    }
    
    if (element.type === 'heading') {
      const fontSize = element.level === 1 ? 18 : element.level === 2 ? 14 : 12;
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', 'bold');
      
      const lines = pdf.splitTextToSize(element.content, maxWidth);
      pdf.text(lines, margin, y);
      y += lines.length * (fontSize * 0.5) + 8;
    } else if (element.type === 'table' && element.rows) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      const colWidth = maxWidth / (element.rows[0]?.length || 1);
      
      for (let rowIdx = 0; rowIdx < element.rows.length; rowIdx++) {
        const row = element.rows[rowIdx];
        if (rowIdx === 0) {
          pdf.setFont('helvetica', 'bold');
        } else {
          pdf.setFont('helvetica', 'normal');
        }
        
        for (let colIdx = 0; colIdx < row.length; colIdx++) {
          const cellX = margin + colIdx * colWidth;
          const cellText = pdf.splitTextToSize(row[colIdx], colWidth - 4);
          pdf.text(cellText, cellX + 2, y);
        }
        y += 8;
        
        if (y > pdf.internal.pageSize.getHeight() - 30) {
          pdf.addPage();
          y = 20;
        }
      }
      y += 5;
    } else {
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const lines = pdf.splitTextToSize(element.content, maxWidth);
      pdf.text(lines, margin, y);
      y += lines.length * 6 + 4;
    }
  }
  
  return pdf.output('blob');
}

/**
 * Convert table content to Excel
 * Uses dynamic import to reduce initial bundle size
 */
export async function convertToXlsx(markdown: string, filename: string): Promise<Blob> {
  // Dynamic import - only loads when function is called
  const XLSX = await import('xlsx');
  
  // Parse tables from markdown
  const lines = markdown.split('\n');
  const data: string[][] = [];
  
  for (const line of lines) {
    if (line.includes('|')) {
      // Skip separator rows
      if (line.match(/^\s*\|?\s*[-:]+\s*\|/)) continue;
      
      const cells = line.split('|')
        .filter(cell => cell.trim() !== '')
        .map(cell => cell.trim());
      
      if (cells.length > 0) {
        data.push(cells);
      }
    }
  }
  
  // If no table found, treat each line as a row
  if (data.length === 0) {
    for (const line of lines) {
      if (line.trim()) {
        data.push([line.trim()]);
      }
    }
  }
  
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  
  const xlsxBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([xlsxBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

/**
 * Convert content to CSV
 */
export function convertToCsv(markdown: string, filename: string): Blob {
  const lines = markdown.split('\n');
  const csvLines: string[] = [];
  
  for (const line of lines) {
    if (line.includes('|')) {
      if (line.match(/^\s*\|?\s*[-:]+\s*\|/)) continue;
      
      const cells = line.split('|')
        .filter(cell => cell.trim() !== '')
        .map(cell => `"${cell.trim().replace(/"/g, '""')}"`);
      
      if (cells.length > 0) {
        csvLines.push(cells.join(','));
      }
    }
  }
  
  return new Blob([csvLines.join('\n')], { type: 'text/csv' });
}

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Convert and download an artifact
 */
export async function downloadArtifact(artifact: DocumentArtifact) {
  let blob: Blob;
  
  switch (artifact.type) {
    case 'docx':
      blob = await convertToDocx(artifact.content, artifact.filename);
      break;
    case 'pdf':
      blob = await convertToPdf(artifact.content, artifact.filename);
      break;
    case 'xlsx':
      blob = await convertToXlsx(artifact.content, artifact.filename);
      break;
    case 'csv':
      blob = convertToCsv(artifact.content, artifact.filename);
      break;
    default:
      throw new Error(`Unsupported document type: ${artifact.type}`);
  }
  
  downloadBlob(blob, artifact.filename);
}
